/**
 * Shared markdown renderer with Mantine styling.
 *
 * Uses react-markdown with remark-gfm for GitHub Flavored Markdown support
 * (tables, strikethrough, task lists). Maps markdown elements to Mantine
 * components for consistent styling.
 */
import { Anchor, Blockquote, Code, Table, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import classes from './MarkdownRenderer.module.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Component mapping from markdown elements to Mantine components.
 * Provides consistent styling across the application.
 */
const components = {
  // Table elements
  table({ children }: { children?: ReactNode }) {
    return <Table>{children}</Table>;
  },
  thead({ children }: { children?: ReactNode }) {
    return <Table.Thead>{children}</Table.Thead>;
  },
  tbody({ children }: { children?: ReactNode }) {
    return <Table.Tbody>{children}</Table.Tbody>;
  },
  tr({ children }: { children?: ReactNode }) {
    return <Table.Tr>{children}</Table.Tr>;
  },
  th({ children }: { children?: ReactNode }) {
    return <Table.Th>{children}</Table.Th>;
  },
  td({ children }: { children?: ReactNode }) {
    return <Table.Td>{children}</Table.Td>;
  },

  // Headings
  h1({ children }: { children?: ReactNode }) {
    return <Title order={1}>{children}</Title>;
  },
  h2({ children }: { children?: ReactNode }) {
    return <Title order={2}>{children}</Title>;
  },
  h3({ children }: { children?: ReactNode }) {
    return <Title order={3}>{children}</Title>;
  },
  h4({ children }: { children?: ReactNode }) {
    return <Title order={4}>{children}</Title>;
  },
  h5({ children }: { children?: ReactNode }) {
    return <Title order={5}>{children}</Title>;
  },
  h6({ children }: { children?: ReactNode }) {
    return <Title order={6}>{children}</Title>;
  },

  // Block elements
  p({ children }: { children?: ReactNode }) {
    return <Text component="p">{children}</Text>;
  },
  blockquote({ children }: { children?: ReactNode }) {
    return <Blockquote>{children}</Blockquote>;
  },
  hr() {
    // Use native hr element for semantic correctness (tests expect <hr>)
    return (
      <hr
        style={{
          margin: 'var(--mantine-spacing-md) 0',
          border: 'none',
          borderTop: '1px solid var(--mantine-color-default-border)',
        }}
      />
    );
  },

  // Code
  code({ children, className }: { children?: ReactNode; className?: string }) {
    // Inline code vs code blocks: pre > code has className with language
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return <Code>{children}</Code>;
  },
  pre({ children }: { children?: ReactNode }) {
    return (
      <Code block className={classes.codeBlock}>
        {children}
      </Code>
    );
  },

  // Links
  a({ children, href }: { children?: ReactNode; href?: string }) {
    return (
      <Anchor href={href} rel="noopener noreferrer" target="_blank">
        {children}
      </Anchor>
    );
  },

  // Lists - use native elements for proper react-markdown integration
  // Mantine List requires specific structure that conflicts with markdown
  ul({ children }: { children?: ReactNode }) {
    return <ul style={{ paddingLeft: 'var(--mantine-spacing-xl)' }}>{children}</ul>;
  },
  ol({ children }: { children?: ReactNode }) {
    return <ol style={{ paddingLeft: 'var(--mantine-spacing-xl)' }}>{children}</ol>;
  },
  li({ children }: { children?: ReactNode }) {
    return <li style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>{children}</li>;
  },
};

/**
 * Renders markdown content with Mantine-styled components.
 *
 * Supports GitHub Flavored Markdown including tables, strikethrough,
 * task lists, and autolinks.
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={className}>
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
